import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { navigateToMember, navigateToSite } from '@flexo/resolvers';
import { timeFormat } from 'd3-time-format';
import { capitalizeFirstLetter } from '@flexo/general';

const formatDate = timeFormat('%d/%m/%Y');

export interface DetailsListItem {
  title: string;
  value: string;
}

export interface DetailsListConfig {
  title: string;
  subtitle?: string;
  isClickable?: boolean;
  dataList: DetailsListItem[];
  onTitleClick?: () => void;
  isInactive?: boolean;
}

export type DetailsListType = 'memberDetails' | 'siteDetails' | 'communityDetails' | 'siteMembers' | 'memberSites';

interface UseDetailsListDataReturn {
  isGroup: boolean;
  expanded: boolean;
  setExpanded: (expanded: boolean) => void;
  singleConfig: DetailsListConfig | null;
  groupConfigs: DetailsListConfig[];
  totalItems: number;
  hasExpandCollapse: boolean;
}

/**
 * Custom hook to handle DetailsList data processing and state management
 * @param type - The type of details list to render
 * @returns Object with processed data and utility functions
 */
export function useDetailsListData(type: DetailsListType): UseDetailsListDataReturn {
  const { t } = useTranslation();
  const modules = useSelector((state: any) => state.modules);
  const metadata = useSelector((state: any) => state.metadata);
  
  const [expanded, setExpanded] = useState(false);

  // Determine if this is a single item or group
  const isGroup = useMemo(() => ['siteMembers', 'memberSites'].includes(type), [type]);

  // Single configuration creation
  const singleConfig = useMemo(() => {
    if (isGroup) return null;

    const selectedEntity = metadata?.selectedEntity as any;

    switch (type) {
      case 'memberDetails':
        return createMemberDetailsConfig(selectedEntity, t, metadata);
      case 'siteDetails':
        return createSiteDetailsConfig(selectedEntity, t);
      case 'communityDetails':
        return createCommunityDetailsConfig(selectedEntity, t);
      default:
        throw new Error(`Unknown single type: ${type}`);
    }
  }, [type, isGroup, metadata, t]);

  // Group configurations creation
  const groupConfigs = useMemo(() => {
    if (!isGroup) return [];

    switch (type) {
      case 'siteMembers':
        return createSiteMembersConfigs(metadata, t);
      case 'memberSites':
        return createMemberSitesConfigs(metadata, t);
      default:
        throw new Error(`Unknown group type: ${type}`);
    }
  }, [type, isGroup, metadata, t]);

  const totalItems = useMemo(() => groupConfigs.length, [groupConfigs]);
  const hasExpandCollapse = useMemo(() => type === 'memberSites' && totalItems > 4, [type, totalItems]);

  return {
    isGroup,
    expanded,
    setExpanded,
    singleConfig,
    groupConfigs,
    totalItems,
    hasExpandCollapse,
  };
}

// Helper functions for creating configurations
function createMemberDetailsConfig(selectedEntity: any, t: any, metadata: any): DetailsListConfig {
  function getMemberName(member: any) {
    return `${member?.memberFirstName} ${member?.memberLastName}` || '';
  }

  function getMemberSites() {
    return (metadata?.entities as any)?.site?.filter((site: any) => site.memberId === selectedEntity?.memberId)?.length || 0;
  }

  const dataList: DetailsListItem[] = [
    {
      title: t('widgets.memberDetailsParameters.memberId'),
      value: selectedEntity?.memberExternalId || '-'
    },
    {
      title: t('widgets.memberDetailsParameters.address'),
      value: (!selectedEntity?.address || !selectedEntity?.city)
        ? '-'
        : `${selectedEntity?.address}, ${selectedEntity?.city}`
    },
    {
      title: t('widgets.memberDetailsParameters.phone'),
      value: selectedEntity?.phone || '-'
    },
    {
      title: t('widgets.memberDetailsParameters.email'),
      value: selectedEntity?.email || '-'
    },
    {
      title: t('widgets.memberDetailsParameters.sitesNumber'),
      value: getMemberSites().toString()
    }
  ];

  return {
    title: t('widgets.memberDetailsParameters.title'),
    subtitle: capitalizeFirstLetter(getMemberName(selectedEntity)),
    isClickable: false,
    dataList
  };
}

function createSiteDetailsConfig(selectedEntity: any, t: any): DetailsListConfig {
  const dataList: DetailsListItem[] = [
    {
      title: t('widgets.siteDetailsParameters.siteName'),
      value: selectedEntity?.siteName || '-'
    },
    {
      title: t('widgets.siteDetailsParameters.joinDate'),
      value: (() => {
        const startDate = selectedEntity?.joinDate || selectedEntity?.startDate;
        const endDate = selectedEntity?.leaveDate;
        const formattedStart = startDate ? formatDate(new Date(startDate)) : '';
        const formattedEnd = endDate && new Date(endDate) <= new Date() 
          ? ` - ${formatDate(new Date(endDate))}` 
          : '';
        return formattedStart + formattedEnd || '-';
      })()
    },
    {
      title: t('widgets.siteDetailsParameters.type'),
      value: t(`widgets.siteDetailsParameters.${selectedEntity?.siteType?.[0]?.siteType || 'default'}`)
    }
  ];

  return {
    title: t('widgets.siteDetailsParameters.title'),
    subtitle: selectedEntity?.pod || '',
    isClickable: false,
    dataList
  };
}

function createCommunityDetailsConfig(selectedEntity: any, t: any): DetailsListConfig {
  const dataList: DetailsListItem[] = [
    {
      title: t('widgets.communityCommunityDetailsParameters.communityId'),
      value: selectedEntity?.communityId || '-'
    },
    {
      title: t('widgets.communityCommunityDetailsParameters.externalId'),
      value: selectedEntity?.externalId || '-'
    },
    {
      title: t('widgets.communityCommunityDetailsParameters.communityCode'),
      value: selectedEntity?.communityCode || '-'
    },
    {
      title: t('widgets.communityCommunityDetailsParameters.communityName'),
      value: selectedEntity?.communityName || '-'
    },
    {
      title: t('widgets.communityCommunityDetailsParameters.communityType'),
      value: selectedEntity?.communityType || '-'
    },
    {
      title: t('widgets.communityCommunityDetailsParameters.startDate'),
      value: selectedEntity?.startDate ? formatDate(new Date(selectedEntity.startDate)) : '-'
    }
  ];

  return {
    title: t('widgets.communityCommunityDetailsParameters.title'),
    isClickable: false,
    dataList
  };
}

function createSiteMembersConfigs(metadata: any, t: any): DetailsListConfig[] {
  const membersList = ([
    ...((metadata?.sortedEntities as any)?.communities || []).reduce((prev: any, next: any) => ([...prev, ...(next?.members || [])]), []),
    ...((metadata as any)?.unassignedMembers || [])
  ])?.filter((member: any) => member?.sites?.some((site: any) => site?.siteId === metadata?.selectedEntityId));

  const { since, client_id, billing_address, phone, email } = t('widgets.siteMembersList', { returnObjects: true }) as any;

  return membersList.map((member: any) => ({
    title: capitalizeFirstLetter(`${member?.memberFirstName} ${member?.memberLastName}`),
    isClickable: true,
    onTitleClick: () => navigateToMember('member', member?.memberId),
    dataList: [
      {
        title: since,
        value: (() => {
          const joinDate = (metadata?.selectedEntity as any)?.joinDate;
          const startDate = (metadata?.selectedEntity as any)?.startDate;
          return joinDate 
            ? formatDate(new Date(joinDate))
            : startDate 
              ? formatDate(new Date(startDate))
              : '-';
        })()
      },
      {
        title: client_id,
        value: member?.memberExternalId || '-'
      },
      {
        title: billing_address,
        value: (!member?.address || !member?.city)
          ? '-'
          : `${member?.address}, ${member?.city}`
      },
      {
        title: phone,
        value: member?.phone || '-'
      },
      {
        title: email,
        value: member?.email || '-'
      }
    ]
  }));
}

function createMemberSitesConfigs(metadata: any, t: any): DetailsListConfig[] {
  const sites = (metadata?.selectedEntity as any)?.sites || [];

  return sites.map((site: any) => ({
    title: site.siteExternalId,
    subtitle: capitalizeFirstLetter(site.siteName),
    isClickable: true,
    isInactive: new Date(site?.endDate) < new Date(),
    onTitleClick: () => navigateToSite('site', site?.siteId),
    dataList: [
      {
        title: t('widgets.memberSitesDetails.address'),
        value: `${site.address}, ${site.city}`
      },
      {
        title: t('widgets.memberSitesDetails.owned'),
        value: `${site.startDate}${new Date(site?.endDate) < new Date() ? ` - ${site?.endDate}` : ''}`
      },
      {
        title: t('widgets.memberSitesDetails.type'),
        value: capitalizeFirstLetter(site.type)
      }
    ]
  }));
}

export default useDetailsListData; 